import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import useSWRImmutable from 'swr/immutable';

import { NEXT_AUTH_STATUSES } from '@/lib/constants';
import { normaliseCargoData } from '@/lib/models/shipment/utils/build-emissions-segments';
import { emissionsCalculatorResultsWarningsState } from '@/state/emissions-calculator';
import { EmissionsCalculatorRequestParams } from '@/types/api-request-types';
import { EmissionsCalculationApiResponse } from '@/types/api-response-types';
import { AsyncDataInterface } from '@/types/context-types';
import { request } from '@/utils/api/request';
import { createEmissionsCalculationUrl } from '@/utils/helpers/api-urls';

interface UseEmissionsCalculationProps extends EmissionsCalculatorRequestParams {
  isEnabled?: boolean;
}

interface UseEmissionsCalculationInterface extends EmissionsCalculationApiResponse, AsyncDataInterface {}

export const useEmissionsCalculation = ({
  segments,
  cargo,
  isEnabled = false,
}: UseEmissionsCalculationProps): UseEmissionsCalculationInterface => {
  const { status } = useSession();
  const normalisedCargo = normaliseCargoData(cargo);
  const setEmissionsResponseHasWarnings = useSetRecoilState(emissionsCalculatorResultsWarningsState);

  // Only create a URL if we're enabled, authenticated, data is valid, and there are no form errors
  const url =
    isEnabled && status === NEXT_AUTH_STATUSES.AUTHENTICATED
      ? createEmissionsCalculationUrl({ segments, cargo: normalisedCargo })
      : null;

  const { data, isLoading, error } = useSWRImmutable<EmissionsCalculationApiResponse>(url, request, {
    errorRetryCount: 1,
  });

  useEffect(() => {
    const apiHasWarnings = !!(data?.data?.warnings && data.data.warnings.length > 0);
    setEmissionsResponseHasWarnings(apiHasWarnings);
  }, [data, setEmissionsResponseHasWarnings]);

  return {
    data: data?.data,
    dateGenerated: data?.dateGenerated,
    isLoading,
    error,
  };
};
